import { useModal } from '@billy/design-system'
import { useCallback } from 'react'

import { ModalId } from '../../../../enums/modalId'
import { useSegment } from '../../../../hooks'
import type { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import type { InvoiceStatus } from '../../enums/invoiceStatus'
import type { QuoteStatus } from '../../enums/quoteStatus'
import type { InvoiceCustomer } from '../../types/invoices/invoiceCustomer'
import type { MissingEInvoiceDetails } from '../../types/missingEInvoiceDetails'
import type { SendAsEInvoiceForm } from '../../types/sendAsEInvoiceForm'
import { isDocumentTypeCreditNote } from '../../utils/isDocumentTypeCreditNote'
import { useDocumentEInvoiceEligibility } from '../eInvoices/useDocumentEInvoiceEligibility'
import { useIsSubscriptionEligibleForEInvoicing } from '../eInvoices/useIsSubscriptionEligibleForEInvoicing'
import { useInvoiceComplianceMode } from '../useInvoiceComplianceMode'
import { useSendAsEInvoice } from './useSendAsEInvoice'

interface UseSendAsEInvoiceActionOptions {
  customer?: InvoiceCustomer
  documentId: string
  documentStatus?: InvoiceStatus | QuoteStatus
  documentType: InvoiceDocumentType
  eInvoiceAddressingLine?: string
  missingEInvoiceDetails?: MissingEInvoiceDetails
  onSuccess?: () => void
  trackingEventName: string
  trackingPayload?: Record<string, unknown>
}

// Shared by ShareDocumentMenu (single document view) and InvoicesTableStaticActions (table quick actions).
export const useSendAsEInvoiceAction = ({
  customer,
  documentId,
  documentStatus,
  documentType,
  eInvoiceAddressingLine,
  missingEInvoiceDetails,
  onSuccess,
  trackingEventName,
  trackingPayload,
}: UseSendAsEInvoiceActionOptions) => {
  const { track } = useSegment()

  const { address: customerAddress, companyName, customerType, isPublicEntity } = customer || {}

  const { isEligibleForEInvoice, isLoading: isLoadingEligibility } = useDocumentEInvoiceEligibility({
    contactCountry: customerAddress?.country,
    contactType: customerType,
    documentStatus,
    documentType,
    isCustomerPublicEntity: isPublicEntity,
  })
  const { isLoadingSubscriptionDetails, isSubscriptionEligibleForEInvoicing } = useIsSubscriptionEligibleForEInvoicing()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const shouldSkipEInvoiceModalForCreditNote = isDocumentTypeCreditNote(documentType) && !!eInvoiceAddressingLine

  const shouldDisplaySendAsEInvoiceOption = isEligibleForEInvoice

  const upgradeSubscriptionModalId = `${documentId}-${ModalId.UpgradeSubscriptionModal}`
  const sendEInvoiceRequirementsModalId = `${documentId}-${ModalId.SendEInvoiceRequirementsModal}`
  const sendAsEInvoiceModalId = `${documentId}-${ModalId.SendAsEInvoiceModal}`

  const { open: openUpgradeSubscriptionModal } = useModal(upgradeSubscriptionModalId)
  const { open: openSendEInvoiceRequirementsModal } = useModal(sendEInvoiceRequirementsModalId)
  const { close: closeSendAsEInvoiceModal, open: openSendAsEInvoiceModal } = useModal(sendAsEInvoiceModalId)

  const { sendAsEInvoice, isProcessing: isProcessingSendingAsEInvoice } = useSendAsEInvoice({ onSuccess })

  const isMissingEInvoiceDetails = missingEInvoiceDetails?.isMissingEInvoiceDetails

  const handleSendAsEInvoiceModalSubmit = useCallback(
    ({ addressingLine, attachmentUuids, name, publicEntityRoutingEnabled }: SendAsEInvoiceForm) => {
      closeSendAsEInvoiceModal()
      sendAsEInvoice({
        addressingLine,
        attachmentUuids,
        companyName: name || '',
        documentType,
        invoiceId: Number(documentId),
        isPublicEntity: !!publicEntityRoutingEnabled,
      })
    },
    [closeSendAsEInvoiceModal, documentId, documentType, sendAsEInvoice],
  )

  const handleSendAsEInvoice = useCallback(() => {
    track(trackingEventName, trackingPayload)

    if (!isSubscriptionEligibleForEInvoicing) {
      openUpgradeSubscriptionModal()
      return
    }

    if (isMissingEInvoiceDetails) {
      openSendEInvoiceRequirementsModal()
      return
    }

    if (isFrenchComplianceMode && !shouldSkipEInvoiceModalForCreditNote) {
      openSendAsEInvoiceModal()
      return
    }

    sendAsEInvoice({
      companyName: companyName || '',
      documentType,
      invoiceId: Number(documentId),
      isPublicEntity: !!isPublicEntity,
    })
  }, [
    companyName,
    documentId,
    documentType,
    shouldSkipEInvoiceModalForCreditNote,
    isFrenchComplianceMode,
    isMissingEInvoiceDetails,
    isPublicEntity,
    isSubscriptionEligibleForEInvoicing,
    openSendAsEInvoiceModal,
    openSendEInvoiceRequirementsModal,
    openUpgradeSubscriptionModal,
    sendAsEInvoice,
    track,
    trackingEventName,
    trackingPayload,
  ])

  return {
    handleSendAsEInvoice,
    handleSendAsEInvoiceModalSubmit,
    isLoadingEligibility,
    isLoadingSubscriptionDetails,
    isProcessingSendingAsEInvoice,
    sendAsEInvoiceModalId,
    sendEInvoiceRequirementsModalId,
    shouldDisplaySendAsEInvoiceOption,
    upgradeSubscriptionModalId,
  }
}
